<script setup lang="ts">
import type { TreeBulkSectionSelectionState } from "@/app/tools/new-section-management/site-sections-tree/bulk-category-assignment/bulk-category-selection";
import type { TSiteSectionsTreeData } from "@/app/tools/new-section-management/site-sections-tree/site-sections-tree.types";
import type { SiteSectionId } from "@/data/common/ids";
import type { IVirtualizedTreeNode } from "@voltalis/ui-kit/components";

import { cn } from "@voltalis/frontend-utils";

import { DATA_TEST_IDS } from "@/constants/data-test-ids/data-test-ids.constant";

import Checkbox from "primevue/checkbox";

const props = defineProps<{
  isPreviewed: boolean;
  section: IVirtualizedTreeNode<TSiteSectionsTreeData>;
  selectionState: TreeBulkSectionSelectionState;
}>();

const emit = defineEmits<{
  (event: "preview", section: IVirtualizedTreeNode<TSiteSectionsTreeData>): void;
  (event: "reset-preview"): void;
  (event: "update-selection", section: IVirtualizedTreeNode<TSiteSectionsTreeData>, isSelected: boolean): void;
}>();

function getSectionId(section: IVirtualizedTreeNode<TSiteSectionsTreeData>): SiteSectionId {
  if (!section.data) {
    throw new Error("Bulk section checkbox requires a section node with data");
  }

  return section.data.id;
}
</script>

<template>
  <span
    :class="
      cn('shrink-0', {
        'bulk-category-descendant-selection-preview': props.isPreviewed,
      })
    "
    @mouseenter="emit('preview', props.section)"
    @mouseleave="emit('reset-preview')"
  >
    <Checkbox
      binary
      :model-value="props.selectionState.isSelected"
      :indeterminate="props.selectionState.isIndeterminate"
      :data-test-id="DATA_TEST_IDS.views.siteSectionTree.bulkCategory.sectionCheckbox(getSectionId(props.section))"
      @click.stop
      @update:model-value="emit('update-selection', props.section, $event)"
    />
  </span>
</template>

<style lang="scss">
.bulk-category-descendant-selection-preview {
  .p-checkbox-box {
    @apply border-brand;
  }
}
</style>
